// Settings → "Weiteres Kind hinzufügen" (A6, siblings). Parent-gated on the
// server; each child needs its own consent because the registration consent
// covered only the first child.

import { useState } from 'react';
import { ApiError, addChildV3 } from '../../api/client';

interface Props {
  onAdded: () => void;
}

const field: React.CSSProperties = {
  width: '100%', padding: '10px 14px', fontSize: 15,
  border: '1.5px solid rgba(120,90,60,0.2)', borderRadius: 8,
  background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit',
};

const label: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' };

export default function AddChildSection({ onAdded }: Props) {
  const [name, setName] = useState('');
  const [klasse, setKlasse] = useState(1);
  const [gender, setGender] = useState('d');
  const [pin, setPin] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const valid = name.trim().length > 0 && consent && (pin === '' || /^\d{4}$/.test(pin));

  async function save() {
    if (!valid || saving) return;
    setSaving(true);
    setMsg('');
    try {
      await addChildV3({ intake: { name: name.trim(), klasse, gender }, kid_pin: pin || null, gdpr_consent: true });
      setMsg(`✓ ${name.trim()} wurde hinzugefügt. Über „Kind wechseln“ kann ${name.trim()} jetzt spielen.`);
      setName(''); setPin(''); setConsent(false);
      onAdded();
    } catch (e) {
      setMsg(e instanceof ApiError && e.status === 409
        ? 'Es sind schon 4 Kinder angelegt — mehr geht nicht.'
        : 'Das hat nicht geklappt. Bitte später noch einmal versuchen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={{
      marginTop: 16, padding: 20, background: '#fff',
      borderRadius: 14, border: '1.5px solid rgba(120,90,60,0.15)',
    }}>
      <div style={{
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
        color: 'var(--ink-muted, #8a7a6a)', textTransform: 'uppercase', marginBottom: 4,
      }}>Weiteres Kind hinzufügen</div>
      <label htmlFor="add-child-name" style={label}>Vorname</label>
      <input id="add-child-name" value={name} maxLength={40} onChange={e => setName(e.target.value)} style={field} />
      <label htmlFor="add-child-klasse" style={label}>Klasse</label>
      <select id="add-child-klasse" value={klasse} onChange={e => setKlasse(Number(e.target.value))} style={field}>
        {[1, 2, 3, 4].map(k => <option key={k} value={k}>Klasse {k}</option>)}
      </select>
      <label htmlFor="add-child-gender" style={label}>Anrede</label>
      <select id="add-child-gender" value={gender} onChange={e => setGender(e.target.value)} style={field}>
        <option value="f">Mädchen</option>
        <option value="m">Junge</option>
        <option value="d">keine Angabe</option>
      </select>
      <label htmlFor="add-child-pin" style={label}>Kinder-PIN (optional, 4 Ziffern)</label>
      <input id="add-child-pin" value={pin} inputMode="numeric" maxLength={4}
        onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} style={field} />
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, fontSize: 13, lineHeight: 1.5 }}>
        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
        <span>
          Ich habe die <a href="/faq.html#datenschutz" target="_blank" rel="noreferrer">Datenschutzhinweise</a> gelesen
          und willige als Erziehungsberechtigte*r auch für dieses Kind in die Verarbeitung seiner Daten ein
          (DSGVO Art. 8), einschließlich der Anfragen an den KI-Anbieter Anthropic in den USA.
        </span>
      </label>
      {msg && (
        <div role="status" style={{ marginTop: 10, fontSize: 12, color: msg.startsWith('✓') ? '#2e7d32' : 'var(--wrong)' }}>
          {msg}
        </div>
      )}
      <button onClick={save} disabled={!valid || saving} style={{
        marginTop: 14, padding: '10px 18px', borderRadius: 10, border: 'none',
        background: valid ? '#4a6ef5' : '#b9c3ef', color: '#fff', fontSize: 14, fontWeight: 700,
        cursor: saving ? 'wait' : valid ? 'pointer' : 'default', fontFamily: 'inherit',
      }}>{saving ? 'Speichere…' : 'Kind hinzufügen'}</button>
    </section>
  );
}
